import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Alert,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack
} from '@mui/material';
import {
    Download as DownloadIcon,
    PlayArrow as RunIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import debounce from 'lodash/debounce';
import { businessReportService } from '../../../services/reportService';
import { bookingService } from '../../../services/bookingService';
import ReportGuide from './ReportGuide';

const NUMERIC_TYPES = ['int', 'number', 'money', 'percent'];

const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' });
const decimal = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const integer = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

export const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }
    switch (type) {
        case 'money':
            return currency.format(Number(value));
        case 'number':
            return decimal.format(Number(value));
        case 'int':
            return integer.format(Number(value));
        case 'percent':
            return `${decimal.format(Number(value))}%`;
        case 'date': {
            // Reformat without Date() so no timezone can shift the day
            const [y, m, d] = String(value).substring(0, 10).split('-');
            return d ? `${d}-${m}-${y}` : String(value);
        }
        default:
            return String(value);
    }
};

const defaultFilters = (report) =>
    Object.fromEntries(report.filters.map((f) => [f.key, f.default ?? '']));

// A filter with visible_if is shown only while the named filters hold one of
// the listed values, e.g. the credit customer picker only for Credit bookings
const isVisible = (filter, values) =>
    Object.entries(filter.visible_if || {}).every(([key, allowed]) => allowed.includes(String(values[key] ?? '')));

const errorMessage = (err, fallback) => {
    const fieldErrors = err.response?.data?.errors;
    if (fieldErrors) {
        return Object.values(fieldErrors)[0]?.[0] || fallback;
    }
    return err.response?.data?.message || err.message || fallback;
};

/**
 * Runs one catalogue report: filter bar, summary strip, paged preview and
 * CSV download. Everything about a report (filters, columns, summary) comes
 * from the server definition, so new reports need no frontend change.
 */
const ReportRunner = ({ report, branches }) => {
    const [filters, setFilters] = useState(() => defaultFilters(report));
    const [customer, setCustomer] = useState(null);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [customerInput, setCustomerInput] = useState('');
    const [customerLoading, setCustomerLoading] = useState(false);

    const [result, setResult] = useState(null);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(50);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setFilters(defaultFilters(report));
        setCustomer(null);
        setCustomerInput('');
        setResult(null);
        setPage(0);
        setError(null);
    }, [report]);

    const searchCustomers = useCallback(async (text) => {
        if (!text || text.length < 2) {
            setCustomerOptions([]);
            return;
        }
        setCustomerLoading(true);
        const response = await bookingService.searchCustomers(text);
        setCustomerOptions(response.success ? response.data || [] : []);
        setCustomerLoading(false);
    }, []);

    const debouncedSearch = useMemo(() => debounce(searchCustomers, 300), [searchCustomers]);

    const setFilter = (key, value) => setFilters((prev) => {
        const next = { ...prev, [key]: value };
        // Clear any filter this change hides, so it does not come back stale
        report.filters.forEach((f) => {
            if (!isVisible(f, next) && next[f.key] !== '') {
                next[f.key] = '';
                if (f.type === 'customer') {
                    setCustomer(null);
                    setCustomerInput('');
                }
            }
        });
        return next;
    });

    // Empty values are left out so the server applies its own defaults
    const visibleFilters = report.filters.filter((f) => isVisible(f, filters));

    // Hidden and empty filters are left out so the server applies its own defaults
    const requestFilters = useMemo(
        () => Object.fromEntries(
            report.filters
                .filter((f) => isVisible(f, filters))
                .map((f) => [f.key, filters[f.key]])
                .filter(([, v]) => v !== '' && v !== null && v !== undefined)
        ),
        [filters, report]
    );

    const load = async (pageIndex, rowsPerPage) => {
        setLoading(true);
        setError(null);
        try {
            const data = await businessReportService.run(report.key, requestFilters, pageIndex + 1, rowsPerPage);
            setResult(data);
            setPage(pageIndex);
        } catch (err) {
            setError(errorMessage(err, 'Failed to run report'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        setDownloading(true);
        setError(null);
        try {
            await businessReportService.download(report.key, requestFilters);
        } catch (err) {
            setError(errorMessage(err, 'Failed to download report'));
        } finally {
            setDownloading(false);
        }
    };

    const renderFilter = (filter) => {
        const value = filters[filter.key] ?? '';
        const label = filter.required ? `${filter.label} *` : filter.label;

        switch (filter.type) {
            case 'date':
                return (
                    <TextField
                        label={label}
                        type="date"
                        size="small"
                        value={value}
                        onChange={(e) => setFilter(filter.key, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                );
            case 'number':
                return (
                    <TextField
                        label={label}
                        type="number"
                        size="small"
                        value={value}
                        onChange={(e) => setFilter(filter.key, e.target.value)}
                        inputProps={{ min: 0 }}
                        fullWidth
                    />
                );
            case 'select':
            case 'branch': {
                const options = filter.type === 'branch'
                    ? [{ value: '', label: 'All Branches' }, ...branches.map((b) => ({ value: b.branch_id, label: b.branch_name }))]
                    : filter.options;
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel id={`${report.key}-${filter.key}`}>{label}</InputLabel>
                        <Select
                            labelId={`${report.key}-${filter.key}`}
                            label={label}
                            value={value}
                            onChange={(e) => setFilter(filter.key, e.target.value)}
                        >
                            {options.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                );
            }
            case 'customer':
                return (
                    <Autocomplete
                        size="small"
                        options={customerOptions}
                        getOptionLabel={(o) => `${o.cust_code} - ${o.cust_name}`}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        filterOptions={(x) => x}
                        loading={customerLoading}
                        value={customer}
                        inputValue={customerInput}
                        onInputChange={(e, text) => {
                            setCustomerInput(text);
                            debouncedSearch(text);
                        }}
                        onChange={(e, option) => {
                            setCustomer(option);
                            setFilter(filter.key, option?.id ?? '');
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={label}
                                placeholder="Type to search"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: <SearchIcon color="action" fontSize="small" sx={{ mr: 0.5 }} />,
                                    endAdornment: customerLoading ? <CircularProgress size={18} /> : params.InputProps.endAdornment
                                }}
                            />
                        )}
                        noOptionsText={customerInput.length < 2 ? 'Type at least 2 characters' : 'No customers found'}
                    />
                );
            default:
                return (
                    <TextField
                        label={label}
                        size="small"
                        value={value}
                        onChange={(e) => setFilter(filter.key, e.target.value)}
                        fullWidth
                    />
                );
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>{report.title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {report.description}
            </Typography>

            <ReportGuide sections={report.guide} />

            <Grid container spacing={2} alignItems="center">
                {visibleFilters.map((filter) => (
                    <Grid item xs={12} sm={6} md={filter.type === 'customer' ? 4 : 2.4} key={filter.key}>
                        {renderFilter(filter)}
                    </Grid>
                ))}
                <Grid item xs={12} md="auto">
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RunIcon />}
                            onClick={() => load(0, perPage)}
                            disabled={loading}
                        >
                            Preview
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={downloading ? <CircularProgress size={18} /> : <DownloadIcon />}
                            onClick={handleDownload}
                            disabled={downloading}
                        >
                            Download CSV
                        </Button>
                    </Stack>
                </Grid>
            </Grid>

            {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>
            )}

            {result && (
                <Box sx={{ mt: 3 }}>
                    {result.summary.length > 0 && (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {result.summary.map((item) => (
                                <Grid item xs={6} sm={4} md={2} key={item.label}>
                                    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {item.label}
                                        </Typography>
                                        <Typography variant={item.type === 'text' ? 'body2' : 'h6'} sx={{ fontWeight: 600 }}>
                                            {formatValue(item.value, item.type)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {result.columns.map((col) => (
                                        <TableCell
                                            key={col.key}
                                            align={NUMERIC_TYPES.includes(col.type) ? 'right' : 'left'}
                                            sx={{ fontWeight: 600, whiteSpace: 'nowrap', bgcolor: 'grey.100' }}
                                        >
                                            {col.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {result.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={result.columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                            No records for these filters.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {result.rows.map((row, i) => (
                                    <TableRow key={i} hover>
                                        {result.columns.map((col) => (
                                            <TableCell
                                                key={col.key}
                                                align={NUMERIC_TYPES.includes(col.type) ? 'right' : 'left'}
                                                sx={{ whiteSpace: col.type === 'text' ? 'normal' : 'nowrap' }}
                                            >
                                                {formatValue(row[col.key], col.type)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={result.total}
                        page={page}
                        rowsPerPage={perPage}
                        rowsPerPageOptions={[25, 50, 100, 200]}
                        onPageChange={(e, p) => load(p, perPage)}
                        onRowsPerPageChange={(e) => {
                            const size = parseInt(e.target.value, 10);
                            setPerPage(size);
                            load(0, size);
                        }}
                    />
                    <Typography variant="caption" color="text.secondary">
                        Generated {formatValue(result.generated_at, 'date')} {String(result.generated_at).substring(11, 16)}.
                        The download contains all {integer.format(result.total)} rows.
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default ReportRunner;
